import { injectAYSdk, stripAYCode } from '../utils/ad-ops/adFunctions'
import { useCpms } from '../composables/ad-ops/cpms/useCpms'
import debug from '../utils/console'
import type { AnalyticsProps, Slot } from '../types/assertive'

const logger = debug.extend('plugins/assertive.client')

export default defineNuxtPlugin({
  name: 'assertive',
  dependsOn: ['openfeature'],
  setup: async (nuxtApp) => {
    const { client } = await useOpenFeature()

    const { ayEntityId } = useRuntimeConfig().public
    const layoutName = client.getStringValue('splittestname', 'control')

    const adSettingsStore = useAdSettingsStore()
    await adSettingsStore.initialized
    const { data: adSettings } = storeToRefs(adSettingsStore)

    const isDynamicFloorEnabled = computed(
      () => adSettings.value.adsDynamicFloorPricing?.isEnabled
    )
    const flooredList = computed(() =>
      Array.from(
        adSettings.value.adsDynamicFloorPricing?.flooredPlacements ?? []
      )
    )
    const analyticsProps: AnalyticsProps = {
      ntp_app_version: getNtpAppVersion(),
      host_browser_app_version: await getHostBrowserAppVersion(),
      type_tag: await getTypeTag(),
    }

    const ayLoaded = await injectAYSdk(
      ayEntityId,
      analyticsProps,
      isDynamicFloorEnabled.value,
      layoutName
    )

    if (!ayLoaded.loading) {
      logger.error('Assertive Yield SDK failed to load')
      return
    }

    logger.info('Assertive Yield loaded')

    // if dynamic floor pricing is enabled collect CPM for each placement
    const cpmMap = new Map<string, number>()
    if (isDynamicFloorEnabled.value) {
      const { getHourlyCpmForPlacement } = useCpms()
      try {
        const cpmPromises = flooredList.value.map(async (placement) => {
          const cpm = await getHourlyCpmForPlacement(placement)
          return { placement, cpm }
        })
        const results = await Promise.allSettled(cpmPromises)

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value.cpm) {
            cpmMap.set(result.value.placement, result.value.cpm)
          } else if (result.status === 'rejected') {
            logger.error('Failed to fetch CPM for placement:', result.reason)
          }
        })
      } catch (error) {
        logger.error('Error fetching CPMs:', error)
      }
    }

    function updateFloorSlotTargeting(adUnitInstanceCode: string, slot: Slot) {
      const placement = stripAYCode(adUnitInstanceCode)

      // If dynamic floor pricing is disabled or this placement is enabled, update targeting and return.
      if (!isDynamicFloorEnabled.value || !cpmMap.has(placement)) {
        slot.updateTargetingFromMap({ placement })
        return
      }

      const defaultCPM = Number(useAppConfig().defaultCpm)
      // Otherwise, set the targeting with the price floor.
      slot.updateTargetingFromMap({
        ay_floor:
          normalizePricePointBucket(cpmMap.get(placement) ?? defaultCPM ?? 0) ??
          '',
        placement,
      })
      logger.info(
        `Updated slot targeting for ${placement} with ay_floor: ${cpmMap.get(placement)}`
      )
    }

    // update targeting for each slot -KVs in GAM360 MUCH match
    window.ayManagerEnv.cmd.push(() => {
      window.ayManagerEnv.onEvent('afterDefineSlot', updateFloorSlotTargeting)
    })

    if (!window.analytics) {
      window.analytics = useAnalytics()
    }

    nuxtApp.provide('ayLoaded', ayLoaded)
  },
})
